import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import useAuthStore from '../store/authStore';
import { mediaSrc } from '../api/media';
import { Sticker } from '../components/Cartoons';
import KidChat from './KidChat';

interface Friend {
  name: string;
  profilePhotoUrl: string | null;
}

interface Group {
  id: number;
  name: string;
  isOwner: boolean;
  memberCount: number;
}

type SocialTab = 'friends' | 'groups' | 'chat';

const KidSocial: React.FC = () => {
  const { userId } = useAuthStore();
  const [params] = useSearchParams();
  const [tab, setTab] = useState<SocialTab>(
    params.get('tab') === 'chat' || params.get('cid') ? 'chat' : 'friends'
  );
  const [friends, setFriends] = useState<Friend[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);

  useEffect(() => {
    api.get(`/api/kids/${userId}/friends`).then((r) => setFriends(r.data || [])).catch(() => {});
    api.get(`/api/kids/${userId}/groups`).then((r) => setGroups(r.data || [])).catch(() => {});
  }, [userId]);

  // switch to the chat sub-tab when navigated here with chat params (e.g. from the bell)
  useEffect(() => {
    if (params.get('tab') === 'chat' || params.get('cid')) setTab('chat');
  }, [params]);

  return (
    <div className="space-y-5">
      {/* Sub-tab switcher */}
      <div className="flex gap-2 bg-white/70 backdrop-blur rounded-full p-1 shadow-sm">
        <button
          onClick={() => setTab('friends')}
          className={`kt-tab flex-1 ${tab === 'friends' ? 'bg-brand-purple text-white shadow' : 'text-gray-500'}`}
        >
          🤝 Friends
        </button>
        <button
          onClick={() => setTab('groups')}
          className={`kt-tab flex-1 ${tab === 'groups' ? 'bg-brand-green text-white shadow' : 'text-gray-500'}`}
        >
          👪 Groups
        </button>
        <button
          onClick={() => setTab('chat')}
          className={`kt-tab flex-1 ${tab === 'chat' ? 'bg-brand-blue text-white shadow' : 'text-gray-500'}`}
        >
          💬 Chat
        </button>
      </div>

      {tab === 'chat' ? (
        <KidChat />
      ) : tab === 'friends' ? (
        friends.length === 0 ? (
          <EmptyCard emoji="🤝" text="No friends yet" sub="Your parent can connect you with friends." />
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {friends.map((f, i) => (
              <div key={i} className="kt-card p-4 flex flex-col items-center gap-2">
                {f.profilePhotoUrl ? (
                  <img
                    src={mediaSrc(f.profilePhotoUrl)}
                    alt={f.name}
                    className="w-16 h-16 rounded-full object-cover ring-2 ring-brand-purple/30"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center text-2xl">🧒</div>
                )}
                <span className="text-xs font-bold text-gray-700 text-center truncate w-full">{f.name}</span>
              </div>
            ))}
          </div>
        )
      ) : groups.length === 0 ? (
        <EmptyCard emoji="👪" text="No groups yet" sub="Your parent can add you to groups." />
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.id} className="kt-card p-4 flex items-center gap-3">
              <Sticker emoji="👪" from="#A7F3D0" to="#6EE7B7" size={48} />
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-gray-800 truncate">{g.name}</p>
                <p className="text-xs text-gray-500">
                  {g.memberCount} member{g.memberCount === 1 ? '' : 's'}
                  {g.isOwner && ' · Owner'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const EmptyCard: React.FC<{ emoji: string; text: string; sub: string }> = ({ emoji, text, sub }) => (
  <div className="kt-card p-8 text-center">
    <Sticker emoji={emoji} from="#E9D5FF" to="#C4B5FD" size={64} className="mx-auto mb-3" />
    <p className="font-display font-bold text-gray-700">{text}</p>
    <p className="text-sm text-gray-500 mt-1">{sub}</p>
  </div>
);

export default KidSocial;
